import React, { useEffect, useState } from 'react';
import {
    Box,
    CircularProgress,
    MenuItem,
    TextField,
    makeStyles,
} from '@material-ui/core';
import type { FormField } from '../interfaces';
import { useField, useFormikContext } from 'formik';
import {
    useLazyGetNextAssigneeForTaskQuery,
    useGetPersonsWithRoleQuery,
} from '../services/rtkApi';
import {
    findCamundaPropertyValue,
    formfieldIsRequired,
    getObjectFromListById,
} from '../utils';
import type { FormValuesType } from './FormContainer';
import useDebounce from '@forvalter/utils/useDebounce';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { getFormFieldStyles } from '../formFieldStyles';
import { useFormContext } from '../context/FormContext';

const useStyles = makeStyles((theme) => ({
    loader: {
        marginRight: theme.spacing(3),
    },
}));

type DynamicAssignValue = {
    candidateGroup: string;
    userId: string;
    userName: string;
};

const tryParseDynamicAssignValue = (
    value?: string,
): DynamicAssignValue | undefined => {
    if (!value) return undefined;

    try {
        return JSON.parse(value) as DynamicAssignValue;
    } catch (e) {
        return undefined;
    }
};

export const DynamicAssignSelector = ({
    field,
    activityId,
    isReadOnly,
    values,
}: {
    field: FormField;
    activityId: string;
    isReadOnly: boolean;
    values: FormValuesType;
}) => {
    const { t } = useTranslation();
    const localClasses = useStyles();
    const formFieldClasses = getFormFieldStyles();
    const [formikField, meta] = useField<string>(field.id as string);
    const { setFieldValue } = useFormikContext();
    const { regionContext } = useFormContext();
    const [shouldIncludeRegion, setShouldIncludeRegion] = useState(true);
    const isRequired = formfieldIsRequired(field, regionContext);
    const dependentField = findCamundaPropertyValue(
        'dependentField',
        field.properties,
    );
    const dependentValue = dependentField
        ? values[dependentField]
        : 'static string';
    const { debouncedValue: debouncedDependendValue, isDebouncing } =
        useDebounce(dependentValue, 3000);

    const [
        getNextCandidateGroup,
        { data: nextCandidateGroup, isLoading: getNextCandidateGroupIsLoading },
    ] = useLazyGetNextAssigneeForTaskQuery();

    const {
        data: selectOptions,
        currentData: currentSelectOptions,
        isLoading: getPersonsIsLoading,
    } = useGetPersonsWithRoleQuery(
        {
            rolle: nextCandidateGroup!,
            region: shouldIncludeRegion ? regionContext : undefined,
        },
        { skip: getNextCandidateGroupIsLoading || !nextCandidateGroup },
    );

    // A new role gets its own attempt with region first
    useEffect(() => {
        setShouldIncludeRegion(true);
    }, [nextCandidateGroup]);

    // Handle case where no persons are returned for the given region. Try again without region.
    useEffect(() => {
        if (!currentSelectOptions || !regionContext) return;

        if (currentSelectOptions.length === 0 && shouldIncludeRegion) {
            setShouldIncludeRegion(false);
        }
    }, [currentSelectOptions, shouldIncludeRegion, regionContext]);

    const debouncedGetNextCandidateGroup = () => {
        if (!activityId || isReadOnly) return;

        getNextCandidateGroup({ activityId: activityId })
            .unwrap()
            .then((data) => {
                const currentValue = tryParseDynamicAssignValue(
                    formikField.value,
                );
                if (currentValue && currentValue.candidateGroup !== data) {
                    setFieldValue(field.id, '');
                }
            });
    };

    useEffect(() => {
        debouncedGetNextCandidateGroup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedDependendValue]);

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = getObjectFromListById(e.target.value, selectOptions);
        if (!nextCandidateGroup || !selected) return;

        const updatedValue: DynamicAssignValue = {
            candidateGroup: nextCandidateGroup,
            userId: selected.id,
            userName: selected.value ?? selected.id,
        };

        setFieldValue(field.id, JSON.stringify(updatedValue));
    };

    const isLoading =
        isDebouncing || getNextCandidateGroupIsLoading || getPersonsIsLoading;
    const value = isReadOnly ? field.value : formikField.value;
    const parsedValue = tryParseDynamicAssignValue(value);
    const selectFieldRole = isReadOnly
        ? parsedValue?.candidateGroup
        : nextCandidateGroup;
    return (
        <Box className={formFieldClasses.formFieldContainer}>
            <TextField
                select={true}
                fullWidth={true}
                className={classNames({
                    [formFieldClasses.editableField]: !isReadOnly,
                    [formFieldClasses.readOnlyField]: isReadOnly,
                })}
                required={isRequired}
                variant="outlined"
                id={field.id}
                label={t(`prosjekt.table.roller.${selectFieldRole}`)}
                value={parsedValue?.userId ?? ''}
                onChange={handleOnChange}
                disabled={isReadOnly || isLoading}
                InputLabelProps={{
                    shrink: true,
                }}
                InputProps={{
                    endAdornment: (
                        <>
                            {isLoading && (
                                <CircularProgress
                                    className={localClasses.loader}
                                    size={20}
                                    color="inherit"
                                />
                            )}
                        </>
                    ),
                }}
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched ? meta.error : ''}
            >
                {selectOptions?.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                        {v.value}
                    </MenuItem>
                )) ?? (
                    <MenuItem
                        key={parsedValue?.userId}
                        value={parsedValue?.userId}
                    >
                        {parsedValue?.userName}
                    </MenuItem>
                )}
            </TextField>
        </Box>
    );
};
